/**
 * @stayai bundle cart guard
 *
 * Removes an entire bundle from the cart when it stops being a valid bundle.
 * A bundle is a set of cart lines sharing a `_bundle_id` property: one parent
 * line (`_bundle_parent`) plus its child product lines.
 *
 * Rules (a bundle failing any one of them is removed in full):
 *   - it has no child lines
 *   - it has no parent line
 *   - its parent line quantity is not exactly 1
 *   - its child quantity total is below `_bundle_min`
 *   - its child quantity total is above `_bundle_max`
 *
 * Bounds come from the parent line's own `_bundle_min` / `_bundle_max` properties,
 * so each bundle carries its own limits. Lines predating those properties fall
 * back to a bundle builder's own `bundle-min` / `bundle-max` attributes when one
 * is on the page. With neither, the quantity rule is skipped rather than guessed.
 * Removal batch-zeroes every line sharing the bundle id in one /cart/update.js
 * request.
 *
 * The cart drawer here is vendor-rendered, so quantity and remove controls
 * cannot be withheld at render time. This runs after any cart mutation instead,
 * whoever made it.
 */
(function () {
  'use strict';

  var CART_MUTATION = /\/cart\/(change|update|add|clear)(\.js)?(\?|$)/;

  var repairing = false;
  var pending = null;

  function debug() {
    if (!window.STAYAI_BUNDLE_GUARD_DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['>>> [stayai-bundle-guard]'].concat(args));
  }

  function isParent(item) {
    var flag = item && item.properties && item.properties._bundle_parent;
    return flag === true || flag === 'true';
  }

  function bundleIdOf(item) {
    return (item && item.properties && item.properties._bundle_id) || null;
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  /**
   * Resolves a bundle bound. The parent line's own property wins. A bundle
   * builder on the page is the fallback for lines added before those properties
   * existed — it carries the same metafield values as attributes. Returns null
   * when neither is available, and the bound is then not enforced.
   * @param {Object} parent - the bundle's parent line item
   * @param {string} property - `_bundle_min` or `_bundle_max`
   * @param {string} attribute - matching attribute on the bundle builder element
   * @returns {number|null}
   */
  function resolveBound(parent, property, attribute) {
    var fromLine = toInt(parent.properties[property], null);
    if (fromLine !== null) return fromLine;

    var builder = document.querySelector('stay-ai-bundle-product[' + attribute + ']');
    return builder ? toInt(builder.getAttribute(attribute), null) : null;
  }

  /**
   * Returns the bundle ids that are no longer valid bundles.
   * @param {Array} items - cart line items
   * @returns {Array} bundle ids to remove
   */
  function findBrokenBundles(items) {
    if (!items || items.length === 0) return [];

    var ids = [];
    items.forEach(function (item) {
      var id = bundleIdOf(item);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (ids.length === 0) return [];

    return ids.filter(function (id) {
      var children = items.filter(function (item) {
        return bundleIdOf(item) === id && !isParent(item);
      });
      var parent = items.filter(function (item) {
        return bundleIdOf(item) === id && isParent(item);
      })[0];

      if (children.length === 0 || !parent) {
        debug('broken', id, !parent ? 'no parent line' : 'no child lines');
        return true;
      }

      // One bundle is one parent unit. Raising the parent's quantity would
      // claim extra bundles without the child products to fill them.
      if (toInt(parent.quantity, 1) !== 1) {
        debug('broken', id, 'parent quantity ' + parent.quantity + ', expected 1');
        return true;
      }

      var min = resolveBound(parent, '_bundle_min', 'bundle-min');
      var max = resolveBound(parent, '_bundle_max', 'bundle-max');
      var count = children.reduce(function (total, item) {
        return total + toInt(item.quantity, 0);
      }, 0);

      // Without real bounds the quantity rule is not enforced: guessing them
      // risks deleting a bundle that was always valid.
      if (min === null && max === null) {
        debug('skipping quantity rule for', id, '- no bounds on the line or on the page');
        return false;
      }

      if ((min !== null && count < min) || (max !== null && count > max)) {
        debug('broken', id, 'child quantity ' + count + ' outside ' + min + '-' + max);
        return true;
      }
      return false;
    });
  }

  /**
   * Batch-zeroes every line belonging to the given bundles.
   * @param {Array} items - cart line items
   * @param {Array} brokenIds - bundle ids to remove
   */
  function removeBundles(items, brokenIds) {
    var updates = {};
    items.forEach(function (item) {
      if (brokenIds.indexOf(bundleIdOf(item)) !== -1) updates[String(item.key)] = 0;
    });
    if (Object.keys(updates).length === 0) return Promise.resolve();

    debug('removing', Object.keys(updates).length, 'lines from', brokenIds.length, 'bundle(s)');
    repairing = true;

    return fetch(Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/update.js ' + response.status);
        return response.json();
      })
      .then(function () {
        refreshCartUi();
      })
      .catch(function (error) {
        debug('removal failed', error);
      })
      .then(function () {
        repairing = false;
      });
  }

  /**
   * Asks whatever is rendering the cart to redraw. The drawer is vendor-owned,
   * so every known refresh path is attempted rather than assuming one.
   */
  function refreshCartUi() {
    var ocu = window.OCUApi;
    try {
      if (ocu && typeof ocu.updateCartDetails === 'function') {
        debug('refresh via OCUApi.updateCartDetails');
        ocu.updateCartDetails();
      } else if (ocu && typeof ocu.updateLqdCart === 'function') {
        debug('refresh via OCUApi.updateLqdCart');
        ocu.updateLqdCart();
      }
    } catch (error) {
      debug('vendor refresh failed', error);
    }

    ['cart:refresh', 'cart:change'].forEach(function (name) {
      document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    });
  }

  /** Reads the cart and removes any bundle that no longer holds together. */
  function check() {
    if (repairing) return;

    return fetch(Shopify.routes.root + 'cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var broken = findBrokenBundles(cart.items);
        if (broken.length === 0) return;
        return removeBundles(cart.items, broken);
      })
      .catch(function (error) {
        debug('check failed', error);
      });
  }

  /** Collapses bursts of cart writes into a single check. */
  function scheduleCheck() {
    if (repairing) return;
    clearTimeout(pending);
    pending = setTimeout(check, 250);
  }

  // Cart writes can come from the theme, the vendor drawer, or any app, so both
  // transports are watched rather than hooking a single component.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = nativeFetch.apply(this, arguments);
    if (CART_MUTATION.test(url)) {
      result.then(function (response) {
        if (response && response.ok) scheduleCheck();
      }).catch(function () {});
    }
    return result;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION.test(url || '')) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) scheduleCheck();
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  // A cart can already be broken on arrival, from an earlier session.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleCheck);
  } else {
    scheduleCheck();
  }
})();
